// The v2 canvas store (zustand) — the single source of truth for the live canvas.
// The grid/DOM is only a view: it renders from here and pushes drag/resize results
// back in. Mutations snapshot for undo and flip `dirty` (the Save button). The
// backend stores only widgetKey/position/players/state — the session-only
// instanceId is minted here and stripped on save (see serialize.ts).

import { create } from "zustand";
import type { CanvasWidget, GridPosition, PlayerRef } from "@catbento/shared";
import type { WidgetInstance, WidgetState } from "./types.js";

/** The 5-point zoom bar, and the default (100%). */
export const ZOOM_SCALES = [0.5, 0.75, 1, 1.25, 1.5] as const;
export const DEFAULT_ZOOM_IDX = 2;

const UNDO_LIMIT = 25;

let idCounter = 0;
/** A session-only instance id. Not persisted — re-minted on hydrate. */
function mintId(): string {
  idCounter += 1;
  return `w${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

interface AddWidgetArgs {
  widgetKey: string;
  position: GridPosition;
  players?: PlayerRef[];
  state?: WidgetState;
}

interface CanvasState {
  instances: WidgetInstance[];
  /** Sticky default player: seeds the next placed needs-player widget. */
  lastPlayer: PlayerRef | null;
  zoomIdx: number;
  dirty: boolean;
  loaded: boolean;
  undoStack: WidgetInstance[][];

  addWidget: (args: AddWidgetArgs) => void;
  removeWidget: (instanceId: string) => void;
  moveWidget: (instanceId: string, position: GridPosition) => void;
  resizeWidget: (instanceId: string, position: GridPosition) => void;
  setWidgetPlayers: (instanceId: string, players: PlayerRef[]) => void;
  /** Patch a widget's players and/or opaque state in ONE undo step (merge/remove
   *  a comparison player updates both players and the colour map together). */
  updateWidget: (instanceId: string, patch: { players?: PlayerRef[]; state?: WidgetState }) => void;
  setZoom: (idx: number) => void;
  undo: () => void;
  /** Remove every widget in ONE undoable step (top-bar Clear). No-op when empty. */
  clearCanvas: () => void;
  hydrate: (widgets: CanvasWidget[]) => void;
  markSaved: () => void;
  reset: () => void;
}

/** Push the current instances onto the undo stack (deep-cloned, capped). */
function pushUndo(state: CanvasState): WidgetInstance[][] {
  const next = [...state.undoStack, structuredClone(state.instances)];
  return next.length > UNDO_LIMIT ? next.slice(next.length - UNDO_LIMIT) : next;
}

export const useCanvasStore = create<CanvasState>((set) => ({
  instances: [],
  lastPlayer: null,
  zoomIdx: DEFAULT_ZOOM_IDX,
  dirty: false,
  loaded: false,
  undoStack: [],

  addWidget: ({ widgetKey, position, players, state }) =>
    set((s) => {
      const inst: WidgetInstance = {
        instanceId: mintId(),
        widgetKey,
        position,
        players: players ?? [],
        state: state ?? {},
      };
      return {
        instances: [...s.instances, inst],
        undoStack: pushUndo(s),
        dirty: true,
        // Placing a widget for a player makes them the sticky default.
        lastPlayer: inst.players[0] ?? s.lastPlayer,
      };
    }),

  removeWidget: (instanceId) =>
    set((s) => ({
      instances: s.instances.filter((i) => i.instanceId !== instanceId),
      undoStack: pushUndo(s),
      dirty: true,
    })),

  moveWidget: (instanceId, position) =>
    set((s) => ({
      instances: s.instances.map((i) => (i.instanceId === instanceId ? { ...i, position } : i)),
      undoStack: pushUndo(s),
      dirty: true,
    })),

  resizeWidget: (instanceId, position) =>
    set((s) => ({
      instances: s.instances.map((i) => (i.instanceId === instanceId ? { ...i, position } : i)),
      undoStack: pushUndo(s),
      dirty: true,
    })),

  setWidgetPlayers: (instanceId, players) =>
    set((s) => ({
      instances: s.instances.map((i) => (i.instanceId === instanceId ? { ...i, players } : i)),
      undoStack: pushUndo(s),
      dirty: true,
      lastPlayer: players[0] ?? s.lastPlayer,
    })),

  updateWidget: (instanceId, patch) =>
    set((s) => ({
      instances: s.instances.map((i) => (i.instanceId === instanceId ? { ...i, ...patch } : i)),
      undoStack: pushUndo(s),
      dirty: true,
      // Only a players change touches the sticky default (state-only patches don't).
      lastPlayer: patch.players ? (patch.players[0] ?? s.lastPlayer) : s.lastPlayer,
    })),

  setZoom: (idx) => set({ zoomIdx: Math.min(Math.max(0, idx), ZOOM_SCALES.length - 1) }),

  undo: () =>
    set((s) => {
      if (s.undoStack.length === 0) return {};
      const prev = s.undoStack[s.undoStack.length - 1];
      return { instances: prev, undoStack: s.undoStack.slice(0, -1), dirty: true };
    }),

  // Clear the whole canvas as a single undo step. Guard the empty case so Clear
  // on an already-empty board doesn't push a pointless undo entry or flip dirty.
  clearCanvas: () =>
    set((s) => {
      if (s.instances.length === 0) return {};
      return { instances: [], undoStack: pushUndo(s), dirty: true };
    }),

  // Load a saved board: mint fresh ids, clear undo + dirty. Done once per session
  // (the shell guards on `loaded`) so switching views never clobbers unsaved edits.
  hydrate: (widgets) =>
    set({
      instances: widgets.map((w) => ({ instanceId: mintId(), ...w })),
      undoStack: [],
      dirty: false,
      loaded: true,
    }),

  markSaved: () => set({ dirty: false }),

  // Sign-out: wipe everything so the next login starts clean (the v1 lesson —
  // a module-singleton store outliving the auth session).
  reset: () =>
    set({
      instances: [],
      lastPlayer: null,
      zoomIdx: DEFAULT_ZOOM_IDX,
      dirty: false,
      loaded: false,
      undoStack: [],
    }),
}));
